import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Compliance() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ type: 'SARS', status: 'pending', due_date: '', notes: '' });

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const { data } = await axios.get(`${API}/compliance`, { withCredentials: true });
      setItems(data);
    } catch (error) {
      toast.error('Failed to load compliance items');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/compliance`, formData, { withCredentials: true });
      toast.success('Compliance item created');
      setShowDialog(false);
      setFormData({ type: 'SARS', status: 'pending', due_date: '', notes: '' });
      loadItems();
    } catch (error) {
      toast.error('Failed to create compliance item');
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    completed: 'bg-green-100 text-green-800 border-green-200',
    overdue: 'bg-red-100 text-red-800 border-red-200'
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="compliance-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="compliance-title">
            Compliance
          </h1>
          <p className="mt-2 text-zinc-600">Track regulatory compliance and deadlines</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-black text-white hover:bg-zinc-800 gap-2" data-testid="add-compliance-button">
              <Plus size={18} /> Add Compliance Item
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Add Compliance Item</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="type" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Type</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({...formData, type: value})}>
                  <SelectTrigger className="mt-2" data-testid="compliance-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SARS">SARS</SelectItem>
                    <SelectItem value="VAT">VAT</SelectItem>
                    <SelectItem value="BEE">BEE</SelectItem>
                    <SelectItem value="UIF">UIF</SelectItem>
                    <SelectItem value="COIDA">COIDA</SelectItem>
                    <SelectItem value="CIPC">CIPC Annual Return</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="due_date" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  data-testid="compliance-due-date-input"
                  className="mt-2 border-zinc-300 hover:border-black transition-colors"
                />
              </div>
              <div>
                <Label htmlFor="notes" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  data-testid="compliance-notes-input"
                  className="mt-2 border-zinc-300 hover:border-black transition-colors"
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full bg-black text-white hover:bg-zinc-800" data-testid="submit-compliance-button">
                Create Item
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="compliance-table">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Type</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Due Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {items.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-zinc-500" data-testid="no-compliance-items">
                    No compliance items yet. Add your first item to get started.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="hover:bg-zinc-50 transition-colors" data-testid={`compliance-row-${item.id}`}>
                    <td className="px-6 py-4 font-medium text-zinc-900">{item.type}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold border ${statusColors[item.status]}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-zinc-600 text-sm">
                      {item.due_date ? new Date(item.due_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-zinc-600 text-sm">{item.notes || 'N/A'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
